// The lead flow's analytics scope, modeled on the quote flow's: every
// `lead_flow_*` event carries the lead type without each call site
// threading it through. It sits on the app-level `track()` fan-out and
// never talks to a vendor itself. Properties are small scalars only:
// never an answer value, a name, a number or an id. The events and their
// properties are defined in ./events.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type FC,
  type ReactNode,
} from "react";

import { useAnalytics } from "analytics/AnalyticsContext";

import type { Answers } from "./answers";
import type { StepEntry } from "./entry";
import {
  UNKNOWN_LEAD_TYPE,
  leadFlowEvents,
  leadTypeOf,
  type EndScreen,
  withLeadType,
  type LeadFlowEvent,
} from "./events";
import { isAtNeedLead, type LeadKind, type QuestionStepId } from "./flow";
import type { SubmitFailureReason, SubmitOutcome } from "./submit";

interface LeadFlowAnalytics {
  started(): void;
  stepViewed(view: {
    step: QuestionStepId;
    entry: StepEntry;
    index: number;
    total: number;
  }): void;
  /** `answers` are the ones being committed, which can say what kind of
   * lead this is before the screen has re-rendered with them. */
  stepCompleted(step: QuestionStepId, answers: Answers): void;
  stepInvalid(step: QuestionStepId): void;
  outOfArea(answers: Answers): void;
  endViewed(screen: EndScreen): void;
  submitted(outcome: SubmitOutcome, reason?: SubmitFailureReason): void;
  confirmPromptShown(): void;
}

const LeadFlowAnalyticsContext = createContext<LeadFlowAnalytics | undefined>(
  undefined,
);

export const LeadFlowAnalyticsProvider: FC<{
  /** Null until the first answer says what kind of lead this is. */
  leadKind: LeadKind | null;
  children: ReactNode;
}> = ({ leadKind, children }) => {
  const { track: trackAppEvent } = useAnalytics();
  const leadKindRef = useRef(leadKind);
  leadKindRef.current = leadKind;

  const value = useMemo<LeadFlowAnalytics>(() => {
    const track = (
      event: LeadFlowEvent,
      leadType: string = leadKindRef.current ?? UNKNOWN_LEAD_TYPE,
    ) => {
      const { name, properties } = withLeadType(event, leadType);
      trackAppEvent(name, properties);
    };
    return {
      started: () => track(leadFlowEvents.started()),
      stepViewed: (view) => track(leadFlowEvents.stepViewed(view)),
      stepCompleted: (step, answers) =>
        track(leadFlowEvents.stepCompleted(step), leadTypeOf(answers)),
      stepInvalid: (step) => track(leadFlowEvents.stepInvalid(step)),
      outOfArea: (answers) =>
        track(
          leadFlowEvents.outOfArea(isAtNeedLead(answers)),
          leadTypeOf(answers),
        ),
      endViewed: (screen) => track(leadFlowEvents.endViewed(screen)),
      submitted: (outcome, reason) =>
        track(leadFlowEvents.submitted(outcome, reason)),
      confirmPromptShown: () => track(leadFlowEvents.confirmPromptShown()),
    };
  }, [trackAppEvent]);

  return (
    <LeadFlowAnalyticsContext.Provider value={value}>
      {children}
    </LeadFlowAnalyticsContext.Provider>
  );
};

/** Sends an event from a screen outside the lead form, such as the code
 * screen, where there is no lead type to add. */
export function useTrackLeadFlowEvent(): (event: LeadFlowEvent) => void {
  const { track } = useAnalytics();
  return useCallback(
    ({ name, properties }) => track(name, properties),
    [track],
  );
}

export function useLeadFlowAnalytics(): LeadFlowAnalytics {
  const context = useContext(LeadFlowAnalyticsContext);
  if (context === undefined) {
    throw new Error(
      "useLeadFlowAnalytics must be used within a LeadFlowAnalyticsProvider",
    );
  }
  return context;
}
